import { z } from "zod";

export enum MediaKind {
	IMAGE = "IMAGE",
	VIDEO = "VIDEO",
	BOTH = "BOTH",
}

export enum FormatPresetScope {
	GLOBAL = "GLOBAL",
	COMPANY = "COMPANY",
}

export enum AiModelUseCase {
	PHASE_IMAGE = "PHASE_IMAGE",
	STORYBOARD = "STORYBOARD",
	SCENE_IMAGE = "SCENE_IMAGE",
	NARRATION_TTS = "NARRATION_TTS",
	SCENE_VIDEO = "SCENE_VIDEO",
	VIDEO_EXPORT = "VIDEO_EXPORT",
	/** A background track for the music library — platform-level, not a scenario's. */
	MUSIC = "MUSIC",
}

export enum AiModelProvider {
	OPENAI = "OPENAI",
	ELEVENLABS = "ELEVENLABS",
	BLACK_FOREST = "BLACK_FOREST",
	INTERNAL = "INTERNAL",
	OPENROUTER = "OPENROUTER",
}

export enum PricingUnit {
	PER_IMAGE = "PER_IMAGE",
	PER_SECOND = "PER_SECOND",
	PER_SCENE = "PER_SCENE",
	PER_1K_TOKENS = "PER_1K_TOKENS",
	PER_TRACK = "PER_TRACK",
	PER_EXPORT = "PER_EXPORT",
}

export enum QualityTier {
	BULK = "BULK",
	HERO = "HERO",
}

export const FormatPresetSchema = z.object({
	id: z.number(),
	key: z.string(),
	label: z.string(),
	width: z.number(),
	height: z.number(),
	aspectRatio: z.string(),
	mediaKind: z.nativeEnum(MediaKind),
	scope: z.nativeEnum(FormatPresetScope),
	companyId: z.number().nullable().optional(),
	enabled: z.boolean(),
	sortOrder: z.number(),
});

export const AiModelSchema = z.object({
	id: z.number(),
	useCase: z.nativeEnum(AiModelUseCase),
	provider: z.nativeEnum(AiModelProvider),
	displayName: z.string(),
	unit: z.nativeEnum(PricingUnit),
	unitPriceUsd: z.number(),
	currency: z.string(),
	defaultVariantCount: z.number(),
	qualityTier: z.nativeEnum(QualityTier).nullable().optional(),
	pricesVerified: z.boolean(),
	isDefault: z.boolean(),
	enabled: z.boolean(),
	sortOrder: z.number(),
	/**
	 * The longest clip this model renders per scene. Absent for models that render no
	 * clips at all, which is why the scene editor keeps a fallback rather than trusting it.
	 */
	maxSceneDurationSeconds: z.number().optional(),
});

export const MusicTrackSchema = z.object({
	id: z.number(),
	name: z.string(),
	description: z.string().nullable().optional(),
	url: z.string().nullable().optional(),
	durationSeconds: z.number().nullable().optional(),
	/** Never empty from the API — the library refuses a row without one. */
	licence: z.string().nullable().optional(),
	sortOrder: z.number(),
	/** Whether a model produced this track rather than an admin uploading a file. */
	aiGenerated: z.boolean().nullable().optional(),
	prompt: z.string().nullable().optional(),
	/** Only sent to a platform admin — the picker lists enabled tracks and nothing else. */
	enabled: z.boolean().nullable().optional(),
});

export const EstimateBreakdownSchema = z.object({
	label: z.string(),
	quantity: z.number(),
	unit: z.nativeEnum(PricingUnit),
	unitPriceUsd: z.number(),
	amountUsd: z.number(),
});

export const EstimateSchema = z.object({
	amountUsd: z.number(),
	currency: z.string(),
	aiModelId: z.number(),
	modelDisplayName: z.string(),
	/** False while the model's price is a placeholder — the UI must say so. */
	pricesVerified: z.boolean(),
	breakdown: z.array(EstimateBreakdownSchema),
});

export type FormatPreset = z.infer<typeof FormatPresetSchema>;
export type AiModel = z.infer<typeof AiModelSchema>;
export type MusicTrack = z.infer<typeof MusicTrackSchema>;
export type MediaEstimate = z.infer<typeof EstimateSchema>;
export type EstimateBreakdown = z.infer<typeof EstimateBreakdownSchema>;

export interface EstimateQuantities {
	variants?: number;
	formats?: number;
	scenes?: number;
	seconds?: number;
	tokens?: number;
}

export enum MediaJobType {
	PHASE_IMAGE = "PHASE_IMAGE",
	STORYBOARD = "STORYBOARD",
	SCENE_IMAGE = "SCENE_IMAGE",
	NARRATION_PREVIEW = "NARRATION_PREVIEW",
	NARRATION_TTS = "NARRATION_TTS",
	SCENE_VIDEO = "SCENE_VIDEO",
	VIDEO_EXPORT = "VIDEO_EXPORT",
	MUSIC_TRACK = "MUSIC_TRACK",
}

export enum MediaJobStatus {
	QUEUED = "QUEUED",
	RUNNING = "RUNNING",
	SUCCEEDED = "SUCCEEDED",
	FAILED = "FAILED",
	CANCELLED = "CANCELLED",
}

export const TERMINAL_JOB_STATUSES: MediaJobStatus[] = [
	MediaJobStatus.SUCCEEDED,
	MediaJobStatus.FAILED,
	MediaJobStatus.CANCELLED,
];

export const MediaJobAssetSchema = z.object({
	id: z.number(),
	variantIndex: z.number(),
	formatPresetId: z.number().nullable().optional(),
	fullFilePath: z.string(),
	width: z.number().nullable().optional(),
	height: z.number().nullable().optional(),
	isSelected: z.boolean(),
	courseFileId: z.number().nullable().optional(),
});

export const MediaJobFormatSchema = z.object({
	id: z.number(),
	label: z.string(),
	width: z.number(),
	height: z.number(),
});

/**
 * Something the author should know about a job that nonetheless succeeded — a narration
 * longer than its trimmed clip, a subtitle style that fell back to another one.
 *
 * Served off the job's result payload rather than a column of its own, so the message is
 * the server's and the phrasing here is only ever a label.
 */
export const MediaJobWarningSchema = z.object({
	code: z.string(),
	/** The server's German prose — the fallback when no phrasing exists for the code. */
	message: z.string(),
	storyboardSceneId: z.number().nullable().optional(),
	/** The message's variables, structured, so the UI phrases the code in its own language. */
	details: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const MediaJobSchema = z.object({
	id: z.number(),
	type: z.nativeEnum(MediaJobType),
	status: z.nativeEnum(MediaJobStatus),
	progress: z.number(),
	courseId: z.number().nullable().optional(),
	phaseDataId: z.number().nullable().optional(),
	wizardSessionId: z.number().nullable().optional(),
	storyboardSceneId: z.number().nullable().optional(),
	model: z
		.object({ id: z.number(), displayName: z.string(), pricesVerified: z.boolean() })
		.nullable()
		.optional(),
	formats: z.array(MediaJobFormatSchema),
	estimatedCostUsd: z.number(),
	actualCostUsd: z.number().nullable().optional(),
	variantCount: z.number(),
	prompt: z.string().nullable().optional(),
	assets: z.array(MediaJobAssetSchema),
	errorCode: z.string().nullable().optional(),
	errorMessage: z.string().nullable().optional(),
	warnings: z.array(MediaJobWarningSchema).nullable().optional(),
	submittedAt: z.string().nullable().optional(),
	finishedAt: z.string().nullable().optional(),
	/** The wizard step the job belongs to — the narration script and its audio share a type. */
	step: z.number().nullable().optional(),
});

export const MediaJobListSchema = z.object({
	items: z.array(MediaJobSchema),
	total: z.number(),
});

/** 202 Accepted wraps the job, matching the brief's `{ job }` envelope. */
export const MediaJobEnvelopeSchema = z.object({ job: MediaJobSchema });

export const ScenarioCostsSchema = z.object({
	totalUsd: z.number(),
	currency: z.string(),
	byUseCase: z.array(z.object({ useCase: z.nativeEnum(AiModelUseCase), amountUsd: z.number() })),
	byMonth: z.array(z.object({ month: z.string(), amountUsd: z.number() })),
	jobs: z.array(
		z.object({
			jobId: z.number(),
			type: z.nativeEnum(MediaJobType),
			status: z.nativeEnum(MediaJobStatus),
			amountUsd: z.number(),
			occurredAt: z.string().nullable().optional(),
		})
	),
});

/**
 * Everything this company has spent on AI media in a window — the backend brief's third
 * rollup.
 *
 * `byCourse` may sum to less than `totalUsd`: a generated music track is billed to the
 * company with no scenario attached, so the page has to be able to say so rather than
 * present a breakdown that silently does not add up.
 */
export const CompanyCostsSchema = z.object({
	totalUsd: z.number(),
	currency: z.string(),
	from: z.string(),
	to: z.string(),
	/** False while any model behind the figures still carries a placeholder price. */
	pricesVerified: z.boolean(),
	byCourse: z.array(
		z.object({ courseId: z.number(), courseName: z.string(), amountUsd: z.number() })
	),
	byUseCase: z.array(z.object({ useCase: z.nativeEnum(AiModelUseCase), amountUsd: z.number() })),
	byMonth: z.array(z.object({ month: z.string(), amountUsd: z.number() })),
});

/** Where one generated file is being used — possibly in a scenario that did not make it. */
export const AssetUsageSchema = z.object({
	courseFileId: z.number(),
	courseId: z.number().nullable().optional(),
	courseName: z.string().nullable().optional(),
	fileAssignment: z.string(),
});

/**
 * One generated file in the media library.
 *
 * `sourceCourse*` is the scenario whose generation paid for it; `usages` is everywhere it is
 * used now. The two differ the moment a file is reused, which is the whole point of the
 * library — and is why the cost report can show one scenario paying for a video another one
 * is playing.
 */
export const LibraryAssetSchema = z.object({
	/** Which table it lives in, and therefore which endpoint acts on it. */
	kind: z.enum(["generated", "uploaded"]),
	id: z.number(),
	jobId: z.number().nullable().optional(),
	jobType: z.nativeEnum(MediaJobType).nullable().optional(),
	sourceCourseId: z.number().nullable().optional(),
	sourceCourseName: z.string().nullable().optional(),
	fileName: z.string().nullable().optional(),
	fullFilePath: z.string(),
	mimeType: z.string().nullable().optional(),
	width: z.number().nullable().optional(),
	height: z.number().nullable().optional(),
	fileLength: z.number().nullable().optional(),
	formatPresetId: z.number().nullable().optional(),
	createdAt: z.string(),
	usages: z.array(AssetUsageSchema),
	/** An upload that defines a custom graphical style — labelled as such. */
	styleReference: z.boolean().optional(),
});

/**
 * One folder of the library's tree, with its subtree.
 *
 * `itemCount` is the files filed directly here, not in the subfolders — the number a tree
 * row shows beside its name. A file nobody has filed is in no folder at all, which is what
 * "Unfiled" means and why the tree carries no row for it.
 */
export const MediaFolderSchema = z.object({
	id: z.number(),
	parentId: z.number().nullable(),
	name: z.string(),
	itemCount: z.number(),
	get children(): z.ZodArray<typeof MediaFolderSchema> {
		return z.array(MediaFolderSchema);
	},
});

/** What create and rename hand back: the folder, without its subtree. */
export const MediaFolderSummarySchema = z.object({
	id: z.number(),
	parentId: z.number().nullable(),
	name: z.string(),
});

export const LibraryScenarioSchema = z.object({ id: z.number(), name: z.string() });

/**
 * The whole tree in one response, plus the scenarios the library's files come from — the
 * scenario filter is built from this, so the standalone page no longer fetches every file
 * a second time just to learn which scenarios it has.
 */
export const MediaFolderTreeSchema = z.object({
	folders: z.array(MediaFolderSchema),
	scenarios: z.array(LibraryScenarioSchema),
});

/** The same shape an upload returns, so generated media is indistinguishable downstream. */
export const CourseFileInfoSchema = z.object({
	id: z.number(),
	fullFilePath: z.string().nullable().optional(),
	fileName: z.string().nullable().optional(),
	fileLength: z.number().nullable().optional(),
	fileType: z.string().nullable().optional(),
	hash: z.string().nullable().optional(),
	fileAssignment: z.string().nullable().optional(),
});

export type MediaJob = z.infer<typeof MediaJobSchema>;
export type MediaJobWarning = z.infer<typeof MediaJobWarningSchema>;
export type MediaJobAsset = z.infer<typeof MediaJobAssetSchema>;
export type MediaJobFormat = z.infer<typeof MediaJobFormatSchema>;
export type ScenarioCosts = z.infer<typeof ScenarioCostsSchema>;
export type CompanyCosts = z.infer<typeof CompanyCostsSchema>;
export type LibraryAsset = z.infer<typeof LibraryAssetSchema>;
export type MediaFolder = z.infer<typeof MediaFolderSchema>;
export type MediaFolderSummary = z.infer<typeof MediaFolderSummarySchema>;
export type LibraryScenario = z.infer<typeof LibraryScenarioSchema>;
export type MediaFolderTree = z.infer<typeof MediaFolderTreeSchema>;
export type AssetUsage = z.infer<typeof AssetUsageSchema>;
export type CourseFileInfo = z.infer<typeof CourseFileInfoSchema>;
